// 세션이 만료된 상태에서 ajax 요청이 401 을 받았을 때, 사용자에게 그 사실을 알린다.
//
// ── 왜 ───────────────────────────────────────────────────────────────────────
// 유휴 4시간이 지나면 서버 세션이 끊긴다(:timeoutable). 그런데 브라우저 화면은 그대로
// 떠 있어서 사용자는 자기가 로그아웃된 줄 모른다.
//
// 그 상태에서 무엇을 눌렀느냐에 따라 겪는 일이 갈렸다:
//   · 페이지 이동  → 302 로 로그인 화면 + "세션이 만료되었습니다"  (정상)
//   · ajax        → 401 만 오고 **아무 일도 안 일어남**            (문제)
//
// 후자는 탭 전환·검색·진행률 폴링처럼 화면 안에서 끝나는 동작이라, 사용자에게는
// "눌렀는데 반응이 없다 / 고장 났다" 로 보인다. 오류 메시지조차 없다.
// (2026-08-25 운영 로그에서 관측 — /workplace/.../register_product/new 가 401.)
//
// ── 왜 서버가 아니라 여기서 ─────────────────────────────────────────────────
// ajax 에 401 을 주는 것 자체는 옳다. 리다이렉트를 주면 그쪽 JS 가 에러 대신 로그인
// 화면 HTML 을 받게 된다. 고칠 곳은 **받는 쪽**이다.
//
// ── 이동 목적지를 하드코딩하지 않는 이유 ────────────────────────────────────
// 버튼은 '/' 로 보낸다. 거기서 서버가 판단한다 — 일반 박스는 로그인 화면으로,
// 데모 박스는 /demo 로(CustomFailureApp). 데모에는 /auth/sign_in 라우트가 아예 없어서
// JS 가 목적지를 정하면 박스마다 틀린다.

const LOGIN_ENTRY = '/'

// 오버레이는 한 번만 띄운다. 진행률 폴링처럼 401 이 연달아 오는 경우에 쌓이지 않도록.
let noticeShown = false

function sameOrigin(url) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

// 데모 체험 종료(DEMO_EXPIRED)는 여기서 다루지 않는다.
//
// ApplicationController 가 게스트 만료를 JSON 401 로 돌려주는데(그쪽 JS 가 에러
// 객체를 기대하기 때문), 그건 "로그인이 끊겼다" 가 아니라 "체험이 끝났다" 다.
// 데모에는 로그인이라는 개념 자체가 없어 "다시 로그인해 주세요" 가 말이 안 된다.
async function isDemoExpired(response) {
  try {
    if (!(response.headers.get('Content-Type') || '').includes('json')) return false
    const data = await response.clone().json()
    return data?.error?.code === 'DEMO_EXPIRED'
  } catch {
    return false
  }
}

function showNotice() {
  if (noticeShown) return
  noticeShown = true

  const overlay = document.createElement('div')
  overlay.setAttribute('role', 'alertdialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', '세션 만료')
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:9999',
    'background:rgba(17,24,39,0.5)',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:16px'
  ].join(';')

  // Tailwind 클래스 대신 인라인 스타일을 쓴다 — JS 가 만들어 넣는 요소라
  // 빌드에 그 클래스가 포함돼 있으리라 기대할 수 없다.
  const box = document.createElement('div')
  box.style.cssText = [
    'background:#fff', 'border-radius:6px', 'max-width:420px', 'width:100%',
    'padding:28px 24px', 'text-align:center',
    'box-shadow:0 10px 25px rgba(0,0,0,0.15)'
  ].join(';')

  const title = document.createElement('p')
  title.textContent = '세션이 만료되었습니다.'
  title.style.cssText = 'margin:0 0 8px;font-size:16px;font-weight:700;color:#111827'

  const desc = document.createElement('p')
  desc.textContent = '오랫동안 사용하지 않아 로그아웃되었습니다. 다시 로그인해 주세요.'
  desc.style.cssText = 'margin:0 0 20px;font-size:14px;line-height:1.5;color:#6b7280'

  // 닫기를 두지 않는다 — 닫아 봐야 그 화면은 이미 아무것도 되지 않는 상태다.
  // 닫을 수 있게 하면 사용자가 같은 자리에서 다시 헤맨다.
  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = '로그인하러 가기'
  button.style.cssText = [
    'width:100%', 'height:44px', 'border:0', 'border-radius:4px',
    'background:#1e2a78', 'color:#fff',
    'font-size:14px', 'font-weight:600', 'cursor:pointer'
  ].join(';')
  button.addEventListener('click', () => { window.location.assign(LOGIN_ENTRY) })

  box.append(title, desc, button)
  overlay.append(box)
  document.body.append(overlay)
  button.focus()
}

// ── fetch ───────────────────────────────────────────────────────────────────
// 전역 훅이 없어 감싸는 수밖에 없다. 응답은 **그대로 통과**시킨다 —
// 호출부의 동작을 바꾸지 않는 것이 이 파일의 전제다.
const originalFetch = window.fetch
window.fetch = async function wrappedFetch(...args) {
  const response = await originalFetch.apply(this, args)

  if (response.status === 401) {
    const input = args[0]
    const url = input instanceof Request ? input.url : String(input ?? '')
    if (sameOrigin(url) && !(await isDemoExpired(response))) showNotice()
  }

  return response
}

// ── jQuery ($.ajax) ─────────────────────────────────────────────────────────
// autocomplete_controller.js 하나가 쓴다. jQuery 가 없는 페이지도 있으므로 확인 후 건다.
document.addEventListener('DOMContentLoaded', () => {
  const jq = window.jQuery
  if (!jq) return

  jq(document).ajaxError((_event, xhr, settings) => {
    if (!xhr || xhr.status !== 401) return
    if (!sameOrigin(settings?.url || '')) return

    try {
      if (JSON.parse(xhr.responseText)?.error?.code === 'DEMO_EXPIRED') return
    } catch {
      // JSON 이 아니면 만료로 본다 — Devise 의 401 은 본문이 비어 있다.
    }

    showNotice()
  })
})
